import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

export const useAdminApi = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState({});

  const authFetch = useCallback(async (url, options = {}) => {
    const token = localStorage.getItem('access_token');
    const headers = {
      ...(options.headers || {}),
      'Authorization': `Bearer ${token}`,
    };
    if (options.body && typeof options.body === 'string') {
      headers['Content-Type'] = headers['Content-Type'] || 'application/json';
    }
    const res = await fetch(url, { ...options, headers });
    if (res.status === 401) {
      toast.error('Сессия истекла. Войдите заново.');
      navigate('/login');
      throw new Error('Unauthorized');
    }
    return res;
  }, [navigate]);

  const setLoadingState = useCallback((key, value) => {
    setLoading(prev => ({ ...prev, [key]: value }));
  }, []);

  // Users API
  const fetchUsers = useCallback(async () => {
    setLoadingState('users', true);
    try {
      const res = await authFetch('/api/admin/users');
      if (!res.ok) throw new Error('Не удалось загрузить пользователей');
      return await res.json();
    } finally {
      setLoadingState('users', false);
    }
  }, [authFetch, setLoadingState]);

  const updateUser = useCallback(async (userId, data) => {
    const res = await authFetch(`/api/admin/users/${userId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
    if (!res.ok) {
      const err = await res.json();
      throw new Error(err.error || 'Ошибка обновления');
    }
    return res.json();
  }, [authFetch]);

  const deleteUser = useCallback(async (userId) => {
    const res = await authFetch(`/api/admin/users/${userId}`, { method: 'DELETE' });
    if (!res.ok) throw new Error('Ошибка удаления');
    return true;
  }, [authFetch]);

  const fetchUserReport = useCallback(async (userId) => {
    const res = await authFetch(`/api/admin/users/${userId}/report`);
    if (!res.ok) throw new Error('Ошибка загрузки отчета');
    return res.json();
  }, [authFetch]);

  const assignTariffToUser = useCallback(async (userId, tariffId) => {
    const res = await authFetch(`/api/admin/users/${userId}/assign-tariff`, {
      method: 'POST',
      body: JSON.stringify({ tariff_id: tariffId }),
    });
    if (!res.ok) {
      const err = await res.json();
      throw new Error(err.error || 'Ошибка назначения тарифа');
    }
    return true;
  }, [authFetch]);

  // Organizations API
  const fetchOrganizations = useCallback(async () => {
    setLoadingState('organizations', true);
    try {
      const res = await authFetch('/api/admin/organizations');
      if (!res.ok) throw new Error('Не удалось загрузить организации');
      return await res.json();
    } finally {
      setLoadingState('organizations', false);
    }
  }, [authFetch, setLoadingState]);

  const createOrganization = useCallback(async (data) => {
    const res = await authFetch('/api/admin/organizations', {
      method: 'POST',
      body: JSON.stringify(data),
    });
    if (!res.ok) {
      const err = await res.json();
      throw new Error(err.error || 'Ошибка создания');
    }
    return res.json();
  }, [authFetch]);

  const updateOrganization = useCallback(async (orgId, data) => {
    const res = await authFetch(`/api/admin/organizations/${orgId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
    if (!res.ok) throw new Error('Ошибка обновления');
    return res.json();
  }, [authFetch]);

  const deleteOrganization = useCallback(async (orgId) => {
    const res = await authFetch(`/api/admin/organizations/${orgId}`, { method: 'DELETE' });
    if (!res.ok) throw new Error('Ошибка удаления');
    return true;
  }, [authFetch]);

  const fetchOrgUsers = useCallback(async (orgId) => {
    const res = await authFetch(`/api/admin/organizations/${orgId}/users`);
    if (!res.ok) throw new Error('Ошибка загрузки');
    return res.json();
  }, [authFetch]);

  const addUserToOrg = useCallback(async (orgId, userId) => {
    const res = await authFetch(`/api/admin/organizations/${orgId}/users`, {
      method: 'POST',
      body: JSON.stringify({ user_id: userId }),
    });
    if (!res.ok) throw new Error('Ошибка добавления');
    return true;
  }, [authFetch]);

  const removeUserFromOrg = useCallback(async (orgId, userId) => {
    const res = await authFetch(`/api/admin/organizations/${orgId}/users/${userId}`, { method: 'DELETE' });
    if (!res.ok) throw new Error('Ошибка удаления');
    return true;
  }, [authFetch]);

  const fetchOrgScenarios = useCallback(async (orgId) => {
    const res = await authFetch(`/api/admin/organizations/${orgId}/scenarios`);
    return res.ok ? res.json() : [];
  }, [authFetch]);

  const assignScenarioToOrg = useCallback(async (orgId, scenarioId) => {
    let res = await authFetch(`/api/admin/organizations/${orgId}/scenarios`, {
      method: 'POST',
      body: JSON.stringify({ scenario_id: scenarioId }),
    });
    if (!res.ok) {
      // Fallback: update scenario directly
      res = await authFetch(`/api/scenarios/${scenarioId}`, {
        method: 'PUT',
        body: JSON.stringify({ organization_id: orgId }),
      });
    }
    return res.ok;
  }, [authFetch]);

  const removeScenarioFromOrg = useCallback(async (orgId, scenarioId) => {
    let res = await authFetch(`/api/admin/organizations/${orgId}/scenarios/${scenarioId}`, { method: 'DELETE' });
    if (!res.ok) {
      res = await authFetch(`/api/scenarios/${scenarioId}`, {
        method: 'PUT',
        body: JSON.stringify({ organization_id: null }),
      });
    }
    return res.ok;
  }, [authFetch]);

  // Scenarios API
  const fetchScenarios = useCallback(async () => {
    setLoadingState('scenarios', true);
    try {
      const res = await authFetch('/api/admin/scenarios');
      if (!res.ok) throw new Error('Не удалось загрузить сценарии');
      return await res.json();
    } finally {
      setLoadingState('scenarios', false);
    }
  }, [authFetch, setLoadingState]);

  const createScenario = useCallback(async (data) => {
    const res = await authFetch('/api/scenarios', {
      method: 'POST',
      body: JSON.stringify(data),
    });
    if (!res.ok) {
      const err = await res.json();
      throw new Error(err.error || 'Ошибка создания');
    }
    return res.json();
  }, [authFetch]);

  const updateScenario = useCallback(async (scenarioId, data) => {
    const res = await authFetch(`/api/scenarios/${scenarioId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
    if (!res.ok) throw new Error('Ошибка обновления');
    return res.json();
  }, [authFetch]);

  const deleteScenario = useCallback(async (scenarioId) => {
    const res = await authFetch(`/api/scenarios/${scenarioId}`, { method: 'DELETE' });
    if (!res.ok) throw new Error('Ошибка удаления');
    return true;
  }, [authFetch]);

  const fetchUniqueSpheres = useCallback(async () => {
    const res = await authFetch('/api/scenarios/unique-spheres');
    return res.ok ? (await res.json()).spheres || [] : [];
  }, [authFetch]);

  // Achievements API
  const fetchAchievements = useCallback(async () => {
    setLoadingState('achievements', true);
    try {
      const res = await authFetch('/api/achievements');
      if (!res.ok) throw new Error('Не удалось загрузить достижения');
      return await res.json();
    } finally {
      setLoadingState('achievements', false);
    }
  }, [authFetch, setLoadingState]);

  const createAchievement = useCallback(async (data) => {
    const res = await authFetch('/api/achievements', {
      method: 'POST',
      body: JSON.stringify(data),
    });
    if (!res.ok) {
      const err = await res.json();
      throw new Error(err.error || 'Ошибка создания');
    }
    return res.json();
  }, [authFetch]);

  const updateAchievement = useCallback(async (achievementId, data) => {
    const res = await authFetch(`/api/achievements/${achievementId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
    if (!res.ok) throw new Error('Ошибка обновления');
    return res.json();
  }, [authFetch]);

  const deleteAchievement = useCallback(async (achievementId) => {
    const res = await authFetch(`/api/achievements/${achievementId}`, { method: 'DELETE' });
    if (!res.ok) throw new Error('Ошибка удаления');
    return true;
  }, [authFetch]);

  const fetchUserAchievements = useCallback(async (userId) => {
    const res = await authFetch(`/api/users/${userId}/achievements`);
    return res.ok ? res.json() : [];
  }, [authFetch]);

  const assignAchievementToUser = useCallback(async (userId, achievementId) => {
    const res = await authFetch('/api/achievements/assign_to_user', {
      method: 'POST',
      body: JSON.stringify({ user_id: userId, achievement_id: achievementId, action: 'assign' }),
    });
    if (!res.ok) throw new Error('Ошибка назначения');
    return true;
  }, [authFetch]);

  const unassignAchievementFromUser = useCallback(async (userId, achievementId) => {
    const res = await authFetch(`/api/achievements/${achievementId}/unassign/${userId}`, { method: 'DELETE' });
    if (!res.ok) throw new Error('Ошибка отмены');
    return true;
  }, [authFetch]);

  const uploadAchievementIcon = useCallback(async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    const token = localStorage.getItem('access_token');
    const res = await fetch('/api/upload/achievement_icon', {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${token}` },
      body: formData,
    });
    if (!res.ok) throw new Error('Ошибка загрузки');
    return (await res.json()).url;
  }, []);

  // Tariffs API
  const fetchTariffs = useCallback(async () => {
    setLoadingState('tariffs', true);
    try {
      const res = await authFetch('/api/admin/tariffs');
      return res.ok ? await res.json() : [];
    } finally {
      setLoadingState('tariffs', false);
    }
  }, [authFetch, setLoadingState]);

  const saveTariff = useCallback(async (tariff, isEdit = false) => {
    const url = isEdit ? `/api/admin/tariffs/${tariff.id}` : '/api/admin/tariffs';
    const res = await authFetch(url, {
      method: isEdit ? 'PUT' : 'POST',
      body: JSON.stringify(tariff),
    });
    if (!res.ok) {
      const err = await res.json();
      throw new Error(err.error || 'Ошибка сохранения');
    }
    return res.json();
  }, [authFetch]);

  const deleteTariff = useCallback(async (tariffId) => {
    const res = await authFetch(`/api/admin/tariffs/${tariffId}`, { method: 'DELETE' });
    if (!res.ok) throw new Error('Ошибка удаления');
    return true;
  }, [authFetch]);

  // Stats API
  const fetchStats = useCallback(async () => {
    const res = await authFetch('/api/admin/stats');
    return res.ok ? res.json() : null;
  }, [authFetch]);

  const fetchDailyStats = useCallback(async (dateFrom, dateTo) => {
    let url = '/api/admin/stats/daily';
    if (dateFrom && dateTo) url += `?from=${dateFrom}&to=${dateTo}`;
    const res = await authFetch(url);
    return res.ok ? res.json() : null;
  }, [authFetch]);

  const fetchRolesStats = useCallback(async () => {
    const res = await authFetch('/api/admin/stats/roles');
    return res.ok ? res.json() : null;
  }, [authFetch]);

  const fetchTopScenarios = useCallback(async () => {
    const res = await authFetch('/api/admin/stats/top-scenarios');
    return res.ok ? res.json() : null;
  }, [authFetch]);

  const fetchAchievementsDist = useCallback(async () => {
    const res = await authFetch('/api/admin/stats/achievements-distribution');
    return res.ok ? res.json() : null;
  }, [authFetch]);

  const fetchTopUsers = useCallback(async () => {
    const res = await authFetch('/api/admin/stats/top-users');
    return res.ok ? res.json() : null;
  }, [authFetch]);

  const fetchUsersByRole = useCallback(async (role) => {
    const res = await authFetch(`/api/admin/users/by_role/${role}`);
    return res.ok ? (await res.json()).users || [] : [];
  }, [authFetch]);

  // Payments API
  const fetchPayments = useCallback(async () => {
    const res = await authFetch('/api/admin/payments');
    if (res.ok) {
      const data = await res.json();
      return { payments: data.payments || [], total: data.total || 0, totalAmount: data.total_amount || 0 };
    }
    return { payments: [], total: 0, totalAmount: 0 };
  }, [authFetch]);

  // Moderators API
  const fetchModerators = useCallback(async () => {
    const res = await authFetch('/api/admin/hierarchy/moderators');
    return res.ok ? res.json() : [];
  }, [authFetch]);

  // Prompt Templates API
  const fetchPromptTemplates = useCallback(async () => {
    const res = await authFetch('/api/prompt-templates');
    if (!res.ok) return [];
    const data = await res.json();
    const builtinPrompt = localStorage.getItem('builtin_system_prompt') || 'Вы — базовая системная роль.';
    const builtin = {
      id: '__builtin__',
      name: 'Системный (дефолтный)',
      description: 'Встроенный системный промпт',
      is_builtin: true,
      content_start: `Вы - Системный (дефолтный). ${builtinPrompt}`,
      content_continue: 'Продолжайте диалог.',
    };
    return [builtin, ...(Array.isArray(data) ? data : [])];
  }, [authFetch]);

  const createPromptTemplate = useCallback(async (data) => {
    const res = await authFetch('/api/prompt-templates', {
      method: 'POST',
      body: JSON.stringify(data),
    });
    if (!res.ok) throw new Error('Ошибка создания');
    return res.json();
  }, [authFetch]);

  const updatePromptTemplate = useCallback(async (templateId, data) => {
    const res = await authFetch(`/api/prompts/templates/${templateId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
    if (!res.ok) throw new Error('Ошибка обновления');
    return res.json();
  }, [authFetch]);

  const deletePromptTemplate = useCallback(async (templateId) => {
    const res = await authFetch(`/api/prompt-templates/${templateId}`, { method: 'DELETE' });
    if (!res.ok) throw new Error('Ошибка удаления');
    return true;
  }, [authFetch]);

  const fetchTemplateVariables = useCallback(async (templateId) => {
    if (!templateId || templateId === '__builtin__') return [];
    const res = await authFetch(`/api/prompts/templates/${templateId}/variables`);
    return res.ok ? res.json() : [];
  }, [authFetch]);

  // Global Variables API
  const fetchGlobalVariables = useCallback(async () => {
    const res = await authFetch('/api/prompts/global-variables');
    return res.ok ? res.json() : [];
  }, [authFetch]);

  const createGlobalVariable = useCallback(async (data) => {
    const res = await authFetch('/api/prompts/global-variables', {
      method: 'POST',
      body: JSON.stringify(data),
    });
    if (!res.ok) throw new Error('Ошибка создания');
    return res.json();
  }, [authFetch]);

  const deleteGlobalVariable = useCallback(async (varId) => {
    const res = await authFetch(`/api/prompts/global-variables/${varId}`, { method: 'DELETE' });
    if (!res.ok) throw new Error('Ошибка удаления');
    return true;
  }, [authFetch]);

  return {
    loading,
    authFetch,
    // Users
    fetchUsers, updateUser, deleteUser, fetchUserReport, assignTariffToUser,
    // Organizations
    fetchOrganizations, createOrganization, updateOrganization, deleteOrganization,
    fetchOrgUsers, addUserToOrg, removeUserFromOrg,
    fetchOrgScenarios, assignScenarioToOrg, removeScenarioFromOrg,
    // Scenarios
    fetchScenarios, createScenario, updateScenario, deleteScenario, fetchUniqueSpheres,
    // Achievements
    fetchAchievements, createAchievement, updateAchievement, deleteAchievement,
    fetchUserAchievements, assignAchievementToUser, unassignAchievementFromUser, uploadAchievementIcon,
    // Tariffs
    fetchTariffs, saveTariff, deleteTariff,
    // Stats
    fetchStats, fetchDailyStats, fetchRolesStats, fetchTopScenarios, fetchAchievementsDist, fetchTopUsers, fetchUsersByRole,
    // Payments
    fetchPayments,
    // Moderators
    fetchModerators,
    // Prompt Templates
    fetchPromptTemplates, createPromptTemplate, updatePromptTemplate, deletePromptTemplate, fetchTemplateVariables,
    // Global Variables
    fetchGlobalVariables, createGlobalVariable, deleteGlobalVariable,
  };
};

export default useAdminApi;
